import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
  Field,
  Textarea,
  Text,
  Spinner,
  makeStyles,
} from '@fluentui/react-components';
import { botContextApi, ContextVersion, Survey } from '../services/api.ts';
import { useToast } from './Toast.tsx';

interface ContextEditorProps {
  open: boolean;
  onClose: () => void;
  survey: Survey | null;
}

const useStyles = makeStyles({
  infoBlock: {
    padding: 'var(--space-sm) var(--space-md)',
    backgroundColor: 'var(--color-background)',
    borderRadius: 'var(--radius-sm)',
  },
  actions: {
    flexWrap: 'wrap',
    gap: 'var(--space-sm)',
  },
});

export default function ContextEditor({ open, onClose, survey }: ContextEditorProps) {
  const s = useStyles();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [contextNotesText, setContextNotesText] = useState('');
  const [isCreatingContext, setIsCreatingContext] = useState(false);

  const { data: surveyContextVersions } = useQuery(
    ['context-versions', survey?.id],
    () => botContextApi.list({ survey_id: survey?.id }),
    { enabled: !!survey?.id && open }
  );

  useEffect(() => {
    if (!open || !surveyContextVersions) return;
    if (surveyContextVersions.length > 0) {
      setContextNotesText(surveyContextVersions[0].context_notes_md || '');
    } else {
      setContextNotesText('');
    }
  }, [open, surveyContextVersions]);

  const createContextVersionMutation = useMutation(botContextApi.create, {
    onSuccess: () => queryClient.invalidateQueries(['context-versions', survey?.id]),
    onError: () => toast.error('Failed to save context'),
  });

  const activateContextVersionMutation = useMutation(botContextApi.activate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['context-versions', survey?.id]);
      queryClient.invalidateQueries('surveys');
    },
    onError: () => toast.error('Failed to activate context version'),
  });

  const handleSaveContext = async () => {
    if (!contextNotesText.trim()) { toast.warning('Please enter context notes'); return; }
    if (!survey) return;

    setIsCreatingContext(true);
    try {
      const hasAny = (surveyContextVersions?.length ?? 0) > 0;
      if (hasAny) {
        let created: any = await createContextVersionMutation.mutateAsync({
          survey_id: survey.id,
          context_notes_md: contextNotesText.trim(),
        });
        let newVersionId: number | undefined = created?.id;
        if (!newVersionId) {
          const list = await botContextApi.list({ survey_id: survey.id });
          const newest = [...(list || [])].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
          newVersionId = newest?.id;
        }
        if (newVersionId) await activateContextVersionMutation.mutateAsync(newVersionId);
      } else {
        await createContextVersionMutation.mutateAsync({
          survey_id: survey.id,
          context_notes_md: contextNotesText.trim(),
        });
      }
      toast.success('Context saved');
      onClose();
    } catch {
      toast.error('Failed to save context');
    } finally {
      setIsCreatingContext(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface style={{ maxWidth: '800px', width: '90vw' }}>
        <DialogBody>
          <DialogTitle>Edit Survey Context</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
              {survey && (
                <div className={s.infoBlock}>
                  <Text size={300} weight="semibold">Survey: </Text>
                  <Text size={300}>{survey.title}</Text>
                </div>
              )}
              <Field label="Context Notes (Markdown)" required>
                <Textarea
                  value={contextNotesText}
                  onChange={(e) => setContextNotesText(e.target.value)}
                  placeholder="Enter context notes in Markdown format..."
                  rows={15}
                  style={{ fontFamily: 'var(--font-mono)', fontSize: '13px' }}
                />
              </Field>
            </div>
          </DialogContent>
          <DialogActions className={s.actions}>
            <Button appearance="secondary" onClick={onClose}>Cancel</Button>
            <Button appearance="primary" onClick={handleSaveContext} disabled={isCreatingContext || !contextNotesText.trim()}>
              {isCreatingContext ? 'Saving...' : 'Save Context'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
